import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom';
import PageHeader from '../../components/pageHeader/PageHeader';
import PageWrapper from '../../components/pageComponents/PageWrapper';
import SearchContainer from '../../components/pageComponents/SearchContainer';
import PageTable from '../../components/pageComponents/PageTable';
import ActionsIcons from '../../components/atoms/ActionsIcons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AuthContext } from '../../hooks/context/authContext';
import styled from "styled-components";
import { useManagePostRequest } from '../../hooks/useManagePostRequest/useManagePostRequest';
import useDidMountEffect from '../../hooks/useDidMountEffect/useDidMountEffect';
import FormSelect from '../../components/atoms/Select';
import ModalEnabled from '../../components/modalComponents/ModalEnabled';
import RevisionInicialGDH from './modales/RevisionInicialGDH';
import VerDetalleEtapasPostulante from './modales/VerDetalleEtapasPostulante';
import AsignarVacante from './modales/AsignarVacante';
import QuitarAsignacionModal from './modales/QuitarAsignacionModal';
import ReasignarResponsableEtapaModal from './modales/ReasignarResponsableEtapaModal';
import EntrevistaPostulante from './modales/EntrevistaPostulante';
import FeedbackDireccion from './modales/FeedbackDireccion';
import PruebaConocimiento from './modales/PruebaConocimiento';
import ResultadoPruebaConocimiento from './modales/ResultadoPruebaConocimiento';
import { listarPostulantesByConvocatoriaAPI, exportarPostulantesAPI } from '../../consumers/backendApisUrls';
import { useGenerarDescargarEliminarArchivo } from '../../hooks/useGenerarDescargarEliminarArchivo/useGenerarDescargarEliminarArchivo';
import {ESTADOS_POSTULACION_TP, ESTAPAS_POSTULACION_TP} from '../../constants/hardCodedConstants';
import ProgramarClaseModelo from './modales/ProgramarClaseModelo';
import ResultadoClaseModelo from './modales/ResultadoClaseModelo';
import RevisionClaseModelo from './modales/RevisionClaseModelo';
import RevisionFirmaGDH from './modales/RevisionFirmaGDH';
import ConfirmarEnvioComunicacionResultado from './modales/ConfirmarEnvioComunicacionResultado';
import ConfirmarEnvioComunicacionResultadoLote from './modales/ConfirmarEnvioComunicacionResultadoLote';
import ConfirmarRetrocederEtapaModal from './modales/ConfirmarRetrocederEtapaModal';

const ROL_GESTION_TRASLADOS_PROMOCIONES = 83;
const ROL_DIRECTOR_COLEGIO = 69;

// TP_Feedback.IdEstadoDireccion = 76 (Observado). Mismo id que GlobalConstants.java
// (intranet-puestos-backend) — GDH devolvió una o más preguntas puntuales y el
// Director tiene que corregirlas. idEtapa/idEstado (de TP_Postulante) NO cambian
// cuando esto pasa, así que hace falta este campo aparte para saber que hay algo
// pendiente de subsanar (23/09/2026).
const ESTADO_FEEDBACK_OBSERVADO = 76;

const ESTADO_ESTILOS = {
    "Abierto": { background: "#e3f2fd", color: "#0d6efd" },
    "Apto": { background: "#e2f6e9", color: "#1a7f4b" },
    "No apto": { background: "#fdeaea", color: "#b02a2a" },
    "Desiste": { background: "#f0f0f0", color: "#555" },
    "Omitido": { background: "#f0f0f0", color: "#555" },
};

const EstadoBadge = ({ estado }) => (
    <span style={{
        display: "inline-block",
        padding: "3px 10px",
        borderRadius: "12px",
        fontSize: "0.85em",
        ...(ESTADO_ESTILOS[estado] ?? {})
    }}>
        {estado}
    </span>
);

const formatearFecha = (value) => {
    if (!value) return "-";
    // ListarPostulantesByConvocatoria (backend real) ya devuelve la fecha formateada
    // dd/mm/yyyy (CONVERT(varchar, ..., 103) en SQL) — no se debe reparsear con
    // `new Date(...)`: JS interpreta "dd/mm/yyyy" como mm/dd/yyyy (formato US), lo
    // que da fechas erróneas para días <=12 (p.ej. "03/09/2026" -> 9 de marzo en vez
    // de 3 de septiembre) o "Invalid Date" para días >12. Si ya viene en ese formato,
    // se muestra tal cual.
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) return value;
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;
    return d.toLocaleDateString("es-PE");
};

const opcionesDesdeData = (items, idKey, desKey) => {
    const vistos = new Map();
    items.forEach(row => {
        if (row[idKey] != null && !vistos.has(row[idKey])) {
            vistos.set(row[idKey], row[desKey]);
        }
    });
    return Array.from(vistos, ([Value, Text]) => ({ Value, Text }));
};

const BandejaPostulantes = () => {
    const { state } = useContext(AuthContext);
    const roleIds = (state?.roleIds ?? []).map(Number);
    const esGDH = roleIds.includes(ROL_GESTION_TRASLADOS_PROMOCIONES);
    const esDirector = roleIds.includes(ROL_DIRECTOR_COLEGIO);
    const history = useHistory();
    const location = useLocation();
    const { nombreConvocatoria, idConvocatoria } = location.state ?? {};

    const [isLoading, setIsLoading] = useState(true);
    const [searchCriteria, setSearchCriteria] = useState("");
    const [paginator, setPaginator] = useState({ CurrentPage: 1 });
    const [formValues, setFormValues] = useState({ Items: [], ExtraData: {}, selectedFilters: { IdEtapa: [], IdEstado: [], IdCargo: [], IdSede: [] } });
    const [modalEnabled, setModalEnabled] = useState({ isEnable: false });
    // HU-TR-08 (ícono de avión): selección para el envío masivo. Se limpia en
    // cada búsqueda nueva (successManageSearchCallback) — no tiene sentido
    // conservar una selección de una página/filtro que ya no se está viendo.
    const [seleccionados, setSeleccionados] = useState([]);

    // idPostulante(s) con un envío en vuelo (individual o parte de un lote) —
    // mientras estén acá, su ícono de avión queda deshabilitado. Mitiga el
    // doble clic/doble envío (28/09/2026); no reemplaza una guardia atómica
    // del lado del backend, que sigue pendiente.
    const [idsEnviandoComunicacion, setIdsEnviandoComunicacion] = useState([]);
    const handleIniciarEnvioComunicacion = (idOrIds) => {
        const ids = Array.isArray(idOrIds) ? idOrIds : [idOrIds];
        setIdsEnviandoComunicacion((prev) => [...new Set([...prev, ...ids])]);
    };
    const handleFinalizarEnvioComunicacion = (idOrIds) => {
        const ids = Array.isArray(idOrIds) ? idOrIds : [idOrIds];
        setIdsEnviandoComunicacion((prev) => prev.filter((id) => !ids.includes(id)));
    };

    const [managePostRequest] = useManagePostRequest();
    const [generarDescargarEliminarArchivo] = useGenerarDescargarEliminarArchivo();

    // Trae la data real de la grilla (por ahora, el JSON estático de
    // ListarPostulantesByConvocatoria). Se dispara al montar y cada vez que cambian
    // los filtros o la página — igual que en GestionTrasladosYPromociones.jsx.
    const fetchPostulantes = async () => {
        const api = listarPostulantesByConvocatoriaAPI();
        const payload = getPayload();
        await managePostRequest(api, payload, successManageSearchCallback);
    };

    const getPayload = () => {
        const sf = formValues.selectedFilters ?? {};
        // 29/09/2026 (a pedido de Jody): el backend ahora filtra por selección múltiple
        // (List<int>) — antes solo aceptaba un único valor y acá se mandaba nomás el
        // primero de la selección, así que elegir más de una opción no cambiaba nada
        // frente a elegir solo la primera. IdConvocatoria viene del location.state con
        // el que se navegó a esta pantalla (ver GestionTrasladosYPromociones.jsx,
        // handleVerPostulantes) — es obligatorio para el endpoint real (09/09/2026).
        return {
            IdConvocatoria: idConvocatoria,
            NombrePostulante: searchCriteria,
            Page: paginator.CurrentPage,
            PageSize: 20,
            IdEtapa: sf.IdEtapa ?? [],
            IdEstado: sf.IdEstado ?? [],
            IdCargo: sf.IdCargo ?? [],
            IdSede: sf.IdSede ?? [],
        };
    };

    // Descarga un Excel con los candidatos de la convocatoria (Colaborador, DNI, cargo, sede,
    // etapa y estado) según los filtros vigentes. Se exportan TODAS las coincidencias, no solo
    // la página actual: el BFF recorre las páginas del listado, así que Page/PageSize de este
    // payload no se usan para eso. Se manda el nombre de la convocatoria solo para rotular el
    // archivo. Mismo patrón que "Descargar excel" de GestionTrasladosYPromociones.jsx.
    const handleDescargarExcel = async () => {
        const payload = { ...getPayload(), Page: 1, NombreConvocatoria: nombreConvocatoria ?? '' };
        await generarDescargarEliminarArchivo(exportarPostulantesAPI(), payload);
    };

    const successManageSearchCallback = ({ data }) => {
        const { Items, ExtraData, TotalItems, FirsItemPage, LastItemPage, TotalPages, CurrentPage } = data;
        setFormValues(lastData => ({ ...lastData, Items, ExtraData }));
        setPaginator({ TotalItems, FirsItemPage, LastItemPage, TotalPages, CurrentPage });
        setSeleccionados([]);
    };

    const handleFilterSelected = (res, key) => {
        setFormValues(lastData => ({ ...lastData, selectedFilters: { ...lastData.selectedFilters, [key]: res } }));
    };

    const handleCleanSearch = () => {
        setFormValues(lastData => ({ ...lastData, selectedFilters: { IdEtapa: [], IdEstado: [], IdCargo: [], IdSede: [] } }));
    };

    const init = async () => {
        setIsLoading(true);
        try {
            await fetchPostulantes();
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        init();
    }, []);

    useDidMountEffect(() => {
        fetchPostulantes();
    }, [paginator.CurrentPage, formValues.selectedFilters]);


    // Único botón de la cabecera: siempre lleva de regreso a
    // GestionTrasladosYPromociones.jsx (push explícito, no goBack, para que
    // funcione igual sin importar desde dónde se haya llegado a esta pantalla).
    const handleAtras = () => {
        history.push('/gestionDeTrasladosYPromociones');
    };

    // 07/09/2026: abre la ficha de Revisión Inicial (RevisionInicialGDH.jsx, ver
    // modales/) — todavía sin conexión a backend (a pedido de Jody, es solo visual por
    // ahora), así que la mayoría de sus campos de solo lectura van a verse vacíos hasta
    // que se arme el mock/endpoint de detalle del postulante.
    const handleRevisionInicial = (row) => {
        console.log("Revisión Inicial", row);
        setModalEnabled({ isEnable: true, component: RevisionInicialGDH, data: { postulante: { ...row, nombreConvocatoria } } });
    };

    // "Ver detalle de postulación" (23/09/2026, a pedido de Jody): abre
    // VerDetalleEtapasPostulante.jsx (ver modales/) — visible solo para el rol
    // Gestión de Traslados y Promociones. Por ahora, igual que RevisionInicialGDH
    // y AsignarVacante, el modal no tiene un endpoint de detalle propio todavía,
    // así que solo va a poder mostrar los datos que ya trae `row`.
    const handleVerDetalleEtapas = (row) => {
        setModalEnabled({ isEnable: true, component: VerDetalleEtapasPostulante, data: { postulante: { ...row, nombreConvocatoria } } });
    };

    // "Asignar vacante" (08/09/2026, a pedido de Jody): abre AsignarVacante.jsx
    // (ver modales/) — corresponde a la etapa "Asignación vacante" del catálogo
    // EtapasPostulacion_TYP (ver modelo-datos-notas.md). Por ahora es solo un
    // boceto visual, todavía sin conexión a backend (a pedido de Jody).
    const handleAsignarVacante = (row) => {
        setModalEnabled({ isEnable: true, component: AsignarVacante, data: { postulante: row } });
    };

    // Reasignar responsable de la etapa (suplencia por postulación y etapa): GDH designa un
    // suplente cuando el responsable de la etapa (hoy el Director de la sede) no está disponible.
    const handleReasignarResponsableEtapa = (row) => {
        setModalEnabled({ isEnable: true, component: ReasignarResponsableEtapaModal, data: { postulante: { ...row, nombreConvocatoria } } });
    };

    // Quitar asignación (Masivo): libera la vacante de un participante con la asignación
    // confirmada (en cualquier punto de la firma y el cierre) y lo devuelve a "Apto" en la etapa
    // anterior (Feedback Dirección en Traslado, Entrevista en Promoción).
    // Pide confirmación en un modal (ver QuitarAsignacionModal).
    const handleQuitarAsignacion = (row) => {
        setModalEnabled({ isEnable: true, component: QuitarAsignacionModal, data: { postulante: { ...row, nombreConvocatoria } } });
    };

    // "Clase modelo" y "Entrevista" (14/09/2026, a pedido de Jody): solo aplican a
    // convocatorias de Promoción (TipoProceso='P') — Traslado no pasa por estas
    // etapas (ver Sql.PostulanteSQL.POSTULANTE_LISTADO_SELECT, columna
    // PuedeValidarse). 25/09/2026: "Entrevista" ya abre su propio modal
    // (EntrevistaPostulante.jsx, ver handleEntrevista más abajo).

    // HU-PR-05 CA-03: configura y deriva la prueba al Módulo de Evaluaciones.
    const handleConfigurarEvaluaciones = (row) => {
        setModalEnabled({ isEnable: true, component: PruebaConocimiento, data: { postulante: { ...row, nombreConvocatoria } } });
    };

    // HU-PR-05 CA-10: ficha de resultados + decisión de GDH.
    //
    // onVolverADerivar es la salida para la prueba que murió del otro lado (la
    // postulación cancelada por Selección o vencida). Ese candidato está en la etapa
    // de Evaluación Académica, donde el ícono "Configurar prueba" ya no se muestra,
    // así que sin este puente quedaría sin forma de volver a rendir.
    const handleResultadoPruebaConocimiento = (row) => {
        setModalEnabled({
            isEnable: true,
            component: ResultadoPruebaConocimiento,
            data: {
                postulante: { ...row, nombreConvocatoria },
                onVolverADerivar: handleConfigurarEvaluaciones,
            },
        });
    };

    const getComponenteClaseModelo = (row) => {
        const idEstado = Number(
            row.idEstado ??
            row.IdEstado ??
            row.idEstadoPostulacion ??
            row.IdEstadoPostulacion
        );
        const idEtapa = Number(
            row.idEtapa ??
            row.IdEtapa ??
            row.idEtapaPostulante ??
            row.IdEtapaPostulante
        );

        // Estas dos ramas exigen la etapa Clase Modelo (5): los estados 13 (Observado),
        // 18 (Enviado) y 71 (Configurado) también los usan otras etapas — la firma de
        // Cierre (8) usa 18 y 13, y la prueba de conocimientos (4) y la entrevista (6)
        // usan 71 —, y sin la etapa el ícono "Clase modelo" salía en esas postulaciones.
        const enClaseModelo = idEtapa === ESTAPAS_POSTULACION_TP.CLASE_MODELO;

        if (enClaseModelo && (idEstado === 71 || idEstado === 13) && esDirector) {
            return ResultadoClaseModelo;
        }

        if (enClaseModelo && idEstado === 18 && esGDH) {
            return RevisionClaseModelo;
        }

        // Programar clase modelo (26/09/2026, a pedido de Jody): solo si la etapa
        // anterior — Evaluación Académica (4) — ya fue superada satisfactoriamente
        // (Apto, 63). Antes esta rama era un catch-all ("cualquier idEstado que no
        // sea 71/13/18"), lo que dejaba programar la clase modelo aunque el
        // postulante todavía no hubiera pasado (o no hubiera aprobado) la prueba de
        // conocimientos. Al llegar aquí con idEtapa === CLASE_MODELO (5, ya
        // programada) esta condición no aplica — ahí entran las dos ramas de arriba.
        if (idEtapa === ESTAPAS_POSTULACION_TP.EVALUACION_ACADEMICA
            && idEstado === ESTADOS_POSTULACION_TP.APTO
            && esGDH) {
            return ProgramarClaseModelo;
        }

        return null;
    };

    const handleClaseModelo = (row) => {
        const component = getComponenteClaseModelo(row);
        if (!component) return;

        const postulante = {
            ...row,
            nombreConvocatoria
        };

        setModalEnabled({
            isEnable: true,
            component,
            data: component === ProgramarClaseModelo
                ? { row: postulante }
                : { postulante }
        });
    };

    const handleEntrevista = (row) => {
        // El modal muestra "Eliminar entrevista" solo a GDH: el backend también lo
        // restringe, pero así no se ofrece una acción que el otro rol no puede usar.
        setModalEnabled({ isEnable: true, component: EntrevistaPostulante, data: { postulante: { ...row, nombreConvocatoria }, esGDH } });
    };


    const handleFeedbackDireccion = (row) => {
        setModalEnabled({ isEnable: true, component: FeedbackDireccion, data: { postulante: { ...row, nombreConvocatoria } } });
    };

    // Reutiliza FeedbackDireccion.jsx con soloLectura=true
    // todos los controles quedan bloqueados/solo-visualización y el footer muestra
    // Apto/No Apto en lugar de Guardar y enviar.
    const handleFeedbackGDH = (row) => {
        setModalEnabled({ isEnable: true, component: FeedbackDireccion, data: { postulante: { ...row, nombreConvocatoria }, soloLectura: true } });
    };

    // HU-TR-07 CA-10: revision del documento firmado por el colaborador.
    const handleRevisionFirma = (row) => {
        setModalEnabled({ isEnable: true, component: RevisionFirmaGDH, data: { postulante: { ...row, nombreConvocatoria } } });
    };

    const handleRetrocederEtapa = (row) => {
        setModalEnabled({
            isEnable: true,
            component: ConfirmarRetrocederEtapaModal,
            data: { postulante: { ...row, nombreConvocatoria } },
        });
    }

    // HU-TR-08 (ícono de avión): comunicación de resultado, individual y en
    // lote. R-01: el registro del resultado nunca envía correo por sí solo —
    // GDH lo dispara acá, desde el Gestor/Monitor de Candidatos.
    const handleEnviarComunicacionResultado = (row) => {
        setModalEnabled({
            isEnable: true,
            component: ConfirmarEnvioComunicacionResultado,
            data: {
                postulante: { ...row, nombreConvocatoria }, nombreConvocatoria,
                onIniciarEnvio: handleIniciarEnvioComunicacion,
                onFinalizarEnvio: handleFinalizarEnvioComunicacion,
            },
        });
    };

    const handleEnviarComunicacionResultadoLote = () => {
        if (seleccionados.length === 0) return;
        setModalEnabled({
            isEnable: true,
            component: ConfirmarEnvioComunicacionResultadoLote,
            data: {
                idsPostulante: seleccionados,
                postulantes: items.filter((row) => seleccionados.includes(row.idPostulante)),
                onIniciarEnvio: handleIniciarEnvioComunicacion,
                onFinalizarEnvio: handleFinalizarEnvioComunicacion,
            },
        });
    };

    const items = formValues.Items || [];

    // Solo GDH puede enviar, y solo las filas que el backend marca como
    // pendientes de comunicar (PuedeEnviarComunicacion — solo Masivo, con un
    // resultado comunicable, sin enviar todavía). Se calcula después de
    // `items` (no antes) porque depende de él.
    const idsSeleccionables = esGDH ? items.filter((row) => row.puedeEnviarComunicacion).map((row) => row.idPostulante) : [];
    const todosSeleccionados = idsSeleccionables.length > 0
        && idsSeleccionables.every((id) => seleccionados.includes(id));

    const handleToggleSeleccion = (idPostulante) => {
        setSeleccionados((prev) => prev.includes(idPostulante)
            ? prev.filter((id) => id !== idPostulante)
            : [...prev, idPostulante]);
    };

    const handleToggleSeleccionarTodos = () => {
        setSeleccionados(todosSeleccionados ? [] : idsSeleccionables);
    };
    const cargoOptions = useMemo(() => opcionesDesdeData(items, "idCargo", "desCargo"), [items]);
    const sedeOptions = useMemo(() => opcionesDesdeData(items, "idSede", "desSede"), [items]);
    const etapaOptions = useMemo(() => opcionesDesdeData(items, "idEtapa", "desEtapa"), [items]);
    const estadoOptions = useMemo(() => opcionesDesdeData(items, "idEstado", "desEstado"), [items]);

    // Anchos "col-sm-N" deben sumar 12 (se aplican como th className, sin
    // contenedor .row de por medio — misma convención que ya usaba esta grilla).
    // Al agregar la columna Etapa se angostaron Fecha (2→1) y Nombre (3→2), y
    // al agregar la columna de selección (HU-TR-08, solo GDH) se angostó Cargo
    // (2→1): 1+1+2+2+2+1+2+1=12.
    const HEADERS = [
        ...(esGDH ? [{
            col: "col-sm-1", label:
                <input type="checkbox" checked={todosSeleccionados}
                    disabled={idsSeleccionables.length === 0}
                    onChange={handleToggleSeleccionarTodos}
                    title="Seleccionar todos los pendientes de comunicar" />
        }] : []),
        { col: "col-sm-1", label: "Fecha Postulación" },
        { col: "col-sm-2", label: "Nombre Postulante" },
        {
            col: "col-sm-2", label:
                <div className='d-flex justify-content-center'>
                    <FormSelect description="Etapa"
                                options={etapaOptions}
                                selectedOptions={formValues.selectedFilters?.IdEtapa ?? []}
                                setSelectedOptions={(res) => handleFilterSelected(res, "IdEtapa")} />
                </div>
        },
        {
            col: "col-sm-2", label:
                <div className='d-flex justify-content-center'>
                    <FormSelect description="Estado"
                                options={estadoOptions}
                                selectedOptions={formValues.selectedFilters?.IdEstado ?? []}
                                setSelectedOptions={(res) => handleFilterSelected(res, "IdEstado")} />
                </div>
        },
        {
            col: esGDH ? "col-sm-1" : "col-sm-2", label:
                <div className='d-flex justify-content-center'>
                    <FormSelect description="Cargo"
                                options={cargoOptions}
                                selectedOptions={formValues.selectedFilters?.IdCargo ?? []}
                                setSelectedOptions={(res) => handleFilterSelected(res, "IdCargo")} />
                </div>
        },
        {
            col: "col-sm-2", label:
                <div className='d-flex justify-content-center'>
                    <FormSelect description="Sede"
                                options={sedeOptions}
                                selectedOptions={formValues.selectedFilters?.IdSede ?? []}
                                setSelectedOptions={(res) => handleFilterSelected(res, "IdSede")} />
                </div>
        },
        { col: "col-sm-1", label: "Acciones" },
    ];

    return (
        <div>
            <PageWrapper isLoading={isLoading}>
                <PageHeader title={nombreConvocatoria ? `Bandeja de Postulantes - ${nombreConvocatoria}` : "Bandeja de Postulantes"} />
                <SearchContainer
                    searchCriteria={searchCriteria}
                    setSearchCriteria={setSearchCriteria}
                    setPaginator={setPaginator}
                    manageSearch={fetchPostulantes}
                    addCleanSearch={handleCleanSearch}>

                    <ButtonsWrapper>
                        <div className="d-flex" style={{ gap: '8px' }}>
                            <button type='button'
                                    className='generic-button-buscador d-flex align-items-center'
                                    style={{ gap: '7px' }}
                                    onClick={handleAtras}>
                                <FontAwesomeIcon icon="angle-left" size="lg" className="hand-pointer" title="Atrás" />Atrás
                            </button>
                            <button type='button'
                                    className='generic-button-buscador'
                                    onClick={handleDescargarExcel}>Descargar excel</button>
                            {esGDH && seleccionados.length > 0 &&
                                <button type='button'
                                        className='generic-button-buscador d-flex align-items-center'
                                        style={{ gap: '7px' }}
                                        disabled={idsEnviandoComunicacion.length > 0}
                                        onClick={handleEnviarComunicacionResultadoLote}>
                                    <FontAwesomeIcon icon={idsEnviandoComunicacion.length > 0 ? "spinner" : "paper-plane"}
                                                     spin={idsEnviandoComunicacion.length > 0} size="lg" />
                                    {`Enviar comunicación (${seleccionados.length})`}
                                </button>
                            }
                        </div>
                    </ButtonsWrapper>
                </SearchContainer>

                <PageTable headers={HEADERS} paginator={paginator} setPaginator={setPaginator}>
                    <tbody className="listar-tbody">
                    {items.map((row) => (
                        <tr key={row.idPostulante}>
                            {esGDH &&
                                <td className="text-center">
                                    {row.puedeEnviarComunicacion &&
                                        <input type="checkbox"
                                               checked={seleccionados.includes(row.idPostulante)}
                                               disabled={idsEnviandoComunicacion.includes(row.idPostulante)}
                                               onChange={() => handleToggleSeleccion(row.idPostulante)} />
                                    }
                                </td>
                            }
                            <td className="text-center">{formatearFecha(row.fechaPostulacion)}</td>
                            <td className="text-center">{row.nombrePostulante}</td>
                            <td className="text-center">{row.desEtapa}</td>
                            <td className="text-center">
                                <EstadoBadge estado={row.desEstado} />
                                {row.suplenteNombre &&
                                    <small className="d-block text-muted mt-1">
                                        Suplente: {row.suplenteNombre}
                                    </small>
                                }
                                {row.seEnvioComunicacionResultado &&
                                    <small className="d-block text-muted mt-1">
                                        Comunicado {row.fechaEnvioComunicacionResultado}
                                    </small>
                                }
                                {row.motivoNoApto &&
                                    <small className="d-block text-muted mt-1">
                                        Motivo: {row.motivoNoApto}
                                    </small>
                                }
                            </td>
                            <td className="text-center">{row.desCargo}</td>
                            <td className="text-center">{row.desSede}</td>
                            <td className="text-center">
                                <ActionsIcons>
                                    {esGDH &&
                                        <FontAwesomeIcon icon="eye" size="md"
                                                         className="hand-pointer"
                                                         title="Ver detalle de postulación"
                                                         onClick={() => handleVerDetalleEtapas(row)} />
                                    }
                                    {formValues.ExtraData?.PuedeRevisarPostulacion
                                        && row.idEtapa === ESTAPAS_POSTULACION_TP.REVISION_INICIAL
                                        && row.idEstado === ESTADOS_POSTULACION_TP.EN_VALIDACION &&
                                        <FontAwesomeIcon icon="clipboard-check" size="md"
                                                         className="hand-pointer"
                                                         title="Revisión Inicial"
                                                         onClick={() => handleRevisionInicial(row)} />
                                    }
                                    {formValues.ExtraData?.PuedeAsignarVacante
                                        && row.puedeValidarse &&
                                        <FontAwesomeIcon icon="check-circle" size="md"
                                                         className="hand-pointer"
                                                         title="Asignar vacante"
                                                         onClick={() => handleAsignarVacante(row)} />
                                    }
                                    {/* Quitar asignación: la regla (asignación confirmada + firma
                                        pendiente, convocatoria no cancelada) viene calculada del
                                        lambda en row.puedeQuitarAsignacion. Mismo permiso que
                                        "Asignar vacante". */}
                                    {formValues.ExtraData?.PuedeAsignarVacante
                                        && row.puedeQuitarAsignacion &&
                                        <FontAwesomeIcon icon="undo" size="md"
                                                         className="hand-pointer"
                                                         title="Quitar asignación de vacante"
                                                         onClick={() => handleQuitarAsignacion(row)} />
                                    }
                                    {/* HU-PR-05. Los dos íconos son excluyentes y dependen
                                        de la etapa: antes de llegar a Evaluación Académica
                                        se configura la prueba; una vez ahí, lo que queda es
                                        ver el resultado. El estado fino (si ya rindió, si
                                        se venció, si lo dieron de baja) lo resuelve cada
                                        modal, que consulta al abrirse. */}
                                    {row.tipoProceso === 'P'
                                        && row.idEtapa === ESTAPAS_POSTULACION_TP.FEEDBACK
                                        && row.idEstado === ESTADOS_POSTULACION_TP.APTO &&
                                        <FontAwesomeIcon icon="sliders-h" size="md"
                                                         className="hand-pointer"
                                                         title="Configurar prueba de conocimientos"
                                                         onClick={() => handleConfigurarEvaluaciones(row)} />
                                    }
                                    {row.tipoProceso === 'P'
                                        && row.idEtapa === ESTAPAS_POSTULACION_TP.EVALUACION_ACADEMICA &&
                                        <FontAwesomeIcon icon="poll" size="md"
                                                         className="hand-pointer"
                                                         title="Resultado prueba de conocimientos"
                                                         onClick={() => handleResultadoPruebaConocimiento(row)} />
                                    }
                                    {row.tipoProceso === 'P' && getComponenteClaseModelo(row) &&
                                        <FontAwesomeIcon icon="chalkboard-teacher" size="md"
                                                         className="hand-pointer"
                                                         title="Clase modelo"
                                                         onClick={() => handleClaseModelo(row)} />
                                    }
                                    {/* "Entrevista" (26/09/2026, a pedido de Jody): visible recién cuando
                                        la etapa Clase Modelo (5) ya fue superada como Apto (63) u Omitida
                                        (65), o cuando ya se está en la etapa Entrevista (6) con una
                                        entrevista Configurada (71) — es decir, ya notificada y pendiente
                                        de calificar. Antes se mostraba para cualquier postulación de
                                        Promoción, sin importar en qué etapa estuviera. */}
                                    {row.tipoProceso === 'P'
                                        && (
                                            (row.idEtapa === ESTAPAS_POSTULACION_TP.CLASE_MODELO
                                                && (row.idEstado === ESTADOS_POSTULACION_TP.APTO || row.idEstado === ESTADOS_POSTULACION_TP.OMITIDO))
                                            || (row.idEtapa === ESTAPAS_POSTULACION_TP.ENTREVISTA && row.idEstado === ESTADOS_POSTULACION_TP.CONFIGURADO)
                                        ) &&
                                        <FontAwesomeIcon icon="user-tie" size="md"
                                                         className="hand-pointer"
                                                         title="Entrevista"
                                                         onClick={() => handleEntrevista(row)} />
                                    }
                                    {/* Suplencia por postulación y etapa: el Director (permiso 588) O su
                                        suplente vigente PARA ESTA FILA (row.esSuplenteVigente, lo calcula
                                        el lambda: suplencia activa + en fechas + turno del Director). */}
                                    {(formValues.ExtraData?.PuedeRegistrarFeedbackDireccion || row.esSuplenteVigente)
                                        && row.idEtapa === ESTAPAS_POSTULACION_TP.REVISION_INICIAL
                                        && row.idEstado === ESTADOS_POSTULACION_TP.APTO &&
                                        <FontAwesomeIcon icon="comment-dots" size="md"
                                                         className="hand-pointer"
                                                         title="Feedback Dirección"
                                                         onClick={() => handleFeedbackDireccion(row)} />
                                    }
                                    {/* Observación por pregunta (23/09/2026): GDH devolvió preguntas
                                        puntuales y el Director tiene que corregirlas. Reusa el mismo
                                        modal/handler que el envío inicial — FeedbackDireccion.jsx ya
                                        distingue subsanación de envío inicial mirando PuedeSubsanar. */}
                                    {(formValues.ExtraData?.PuedeRegistrarFeedbackDireccion || row.esSuplenteVigente)
                                        && row.idEstadoDireccion === ESTADO_FEEDBACK_OBSERVADO &&
                                        <FontAwesomeIcon icon="comment-dots" size="md"
                                                         className="hand-pointer text-danger"
                                                         title="Subsanar Feedback observado"
                                                         onClick={() => handleFeedbackDireccion(row)} />
                                    }
                                    {/* "Reasignar responsable": GDH designa un suplente para ESTA postulación
                                        en SU etapa actual. Solo aparece cuando la etapa tiene un responsable
                                        reasignable (row.rolResponsableEtapa, lo resuelve el lambda). */}
                                    {esGDH && row.rolResponsableEtapa &&
                                        <FontAwesomeIcon icon="exchange-alt" size="md"
                                                         className="hand-pointer"
                                                         title="Reasignar responsable de la etapa"
                                                         onClick={() => handleReasignarResponsableEtapa(row)} />
                                    }
                                    {formValues.ExtraData?.PuedeFinalizarFeedbackGdh
                                        && row.idEtapa === ESTAPAS_POSTULACION_TP.FEEDBACK
                                        && row.idEstado === ESTADOS_POSTULACION_TP.EN_VALIDACION &&
                                        <FontAwesomeIcon icon="comment-dots" size="md"
                                                         className="hand-pointer"
                                                         title="Feedback GDH"
                                                         onClick={() => handleFeedbackGDH(row)} />
                                    }
                                    {/* HU-TR-07 CA-10 a CA-16: revisión del documento
                                        firmado. Basta con la etapa: si el colaborador
                                        todavía no envió, si ya fue observado o si el
                                        traslado cerró lo resuelve el propio modal con el
                                        flag PuedeRevisar que arma el backend. */}
                                    {row.idEtapa === ESTAPAS_POSTULACION_TP.FIRMA_CIERRE &&
                                        <FontAwesomeIcon icon="file-signature" size="md"
                                                         className="hand-pointer"
                                                         title="Revisar firma del colaborador"
                                                         onClick={() => handleRevisionFirma(row)} />
                                    }
                                    {/* HU-TR-08 (ícono de avión): comunicación de resultado.
                                        R-01: manual por GDH, nunca automático. Mientras el envío
                                        de esta fila está en vuelo (individual o en lote), el
                                        ícono queda deshabilitado (spinner) para evitar doble
                                        clic/doble envío — ver ConfirmarEnvioComunicacionResultado(Lote).jsx. */}
                                    {esGDH && row.puedeEnviarComunicacion && (
                                        idsEnviandoComunicacion.includes(row.idPostulante)
                                            ? <FontAwesomeIcon icon="spinner" spin size="md" title="Enviando..." />
                                            : <FontAwesomeIcon icon="paper-plane" size="md"
                                                                className="hand-pointer"
                                                                title="Enviar comunicación de resultado"
                                                                onClick={() => handleEnviarComunicacionResultado(row)} />
                                    )}
                                    
                                    {esGDH && (row.idEtapa === ESTAPAS_POSTULACION_TP.REVISION_INICIAL 
                                        || row.idEtapa === ESTAPAS_POSTULACION_TP.FEEDBACK
                                        || row.idEtapa === ESTAPAS_POSTULACION_TP.EVALUACION_ACADEMICA
                                        || row.idEtapa === ESTAPAS_POSTULACION_TP.CLASE_MODELO
                                        || row.idEtapa === ESTAPAS_POSTULACION_TP.ENTREVISTA )
                                        && (row.idEstado === ESTADOS_POSTULACION_TP.NO_APTO || row.idEstado === ESTADOS_POSTULACION_TP.APTO) && (
                                    <FontAwesomeIcon icon={["fas", "backward"]} size="md"
                                                    className="hand-pointer"
                                                    style={{ color: "rgb(224, 28, 28)" }}
                                                    onClick={() => handleRetrocederEtapa(row)}
                                                    title="Retroceder Etapa" />
                                    )}
                                </ActionsIcons>
                            </td>
                        </tr>
                    ))}

                    </tbody>
                </PageTable>
            </PageWrapper>
            <ModalEnabled modalEnabled={modalEnabled} setModalEnabled={setModalEnabled} refreshPage={fetchPostulantes} />
        </div>
    )
}

export default BandejaPostulantes

const ButtonsWrapper = styled.div`
    display: block;
    @media (max-width: 796px) {
        display: none;
    }
`;